export type EntryStyleTextKey =
  | 'title'
  | 'info_no'
  | 'site'
  | 'body'
  | 'phone_label'
  | 'phone'
  | 'mp_link'
  | 'group_header';

export type EntryStylePreset =
  | 'card_teal'
  | 'card_amber'
  | 'border_navy'
  | 'soft_rose'
  | 'minimal_line'
  | 'classic';

export type EntryStyleText = {
  color: string;
  size_px: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
};

export type EntryStyleConfig = {
  preset: EntryStylePreset;
  card_border_color: string;
  card_bg_color: string;
  card_accent_color: string;
  card_radius_px: number;
  card_padding_px: number;
  card_border_width_px: number;
  title: EntryStyleText;
  info_no: EntryStyleText;
  site: EntryStyleText;
  body: EntryStyleText;
  phone_label: EntryStyleText;
  phone: EntryStyleText;
  mp_link: EntryStyleText;
  group_header: EntryStyleText;
};

export const ENTRY_STYLE_TEXT_KEYS: { key: EntryStyleTextKey; label: string }[] = [
  { key: 'title', label: '标题' },
  { key: 'info_no', label: '信息编号' },
  { key: 'site', label: '来源站点' },
  { key: 'body', label: '正文' },
  { key: 'phone_label', label: '电话标签' },
  { key: 'phone', label: '电话号码' },
  { key: 'mp_link', label: '小程序链接' },
  { key: 'group_header', label: '分组标题' },
];

export const ENTRY_STYLE_PRESETS_UI: {
  key: Exclude<EntryStylePreset, 'classic'>;
  label: string;
  desc: string;
  accent: string;
  bg: string;
  border: string;
}[] = [
  {
    key: 'card_teal',
    label: '青绿卡片',
    desc: '左侧色条 + 浅青底',
    accent: '#0f766e',
    bg: '#f0fdfa',
    border: '#99f6e4',
  },
  {
    key: 'card_amber',
    label: '琥珀暖卡',
    desc: '暖色底与琥珀描边',
    accent: '#d97706',
    bg: '#fffbeb',
    border: '#fcd34d',
  },
  {
    key: 'border_navy',
    label: '深蓝描边',
    desc: '整卡描边，商务风',
    accent: '#1e3a5f',
    bg: '#ffffff',
    border: '#1e3a5f',
  },
  {
    key: 'soft_rose',
    label: '柔粉点缀',
    desc: '浅粉底 + 顶部色带',
    accent: '#e11d48',
    bg: '#fff1f2',
    border: '#fecdd3',
  },
  {
    key: 'minimal_line',
    label: '极简色线',
    desc: '顶部细色线，留白多',
    accent: '#64748b',
    bg: '#ffffff',
    border: '#e2e8f0',
  },
];

function clampSize(n: number): number {
  if (!Number.isFinite(n) || n < 1) return 15;
  return Math.min(28, Math.max(10, Math.round(n)));
}

function normColor(raw: unknown, fallback: string): string {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return /^#[0-9A-Fa-f]{6}$/.test(s) ? s.toLowerCase() : fallback;
}

function defaultText(over: Partial<EntryStyleText> = {}): EntryStyleText {
  return {
    color: normColor(over.color, '#334155'),
    size_px: clampSize(over.size_px ?? 15),
    bold: Boolean(over.bold),
    italic: Boolean(over.italic),
    underline: Boolean(over.underline),
  };
}

function presetDefaults(preset: EntryStylePreset): EntryStyleConfig {
  const base: EntryStyleConfig = {
    preset,
    card_border_color: '#e2e8f0',
    card_bg_color: '#ffffff',
    card_accent_color: '#0f766e',
    card_radius_px: 8,
    card_padding_px: 14,
    card_border_width_px: 1,
    title: defaultText({ color: '#111827', size_px: 17, bold: true }),
    info_no: defaultText({ color: '#64748b', size_px: 12 }),
    site: defaultText({ color: '#94a3b8', size_px: 12 }),
    body: defaultText({ color: '#334155', size_px: 15 }),
    phone_label: defaultText({ color: '#94a3b8', size_px: 13 }),
    phone: defaultText({ color: '#475569', size_px: 14, bold: true }),
    mp_link: defaultText({ color: '#0f766e', size_px: 15, bold: true }),
    group_header: defaultText({ color: '#0f766e', size_px: 15, bold: true }),
  };

  switch (preset) {
    case 'card_teal':
      base.card_border_color = '#99f6e4';
      base.card_bg_color = '#f0fdfa';
      base.card_accent_color = '#0f766e';
      base.title.color = '#115e59';
      base.mp_link.color = '#0d9488';
      break;
    case 'card_amber':
      base.card_border_color = '#fcd34d';
      base.card_bg_color = '#fffbeb';
      base.card_accent_color = '#d97706';
      base.title.color = '#92400e';
      base.site.color = '#b45309';
      base.mp_link.color = '#b45309';
      base.group_header.color = '#b45309';
      break;
    case 'border_navy':
      base.card_border_color = '#1e3a5f';
      base.card_bg_color = '#ffffff';
      base.card_accent_color = '#1e3a5f';
      base.card_border_width_px = 2;
      base.title.color = '#1e3a5f';
      base.mp_link.color = '#2563eb';
      base.group_header.color = '#1e3a5f';
      break;
    case 'soft_rose':
      base.card_border_color = '#fecdd3';
      base.card_bg_color = '#fff1f2';
      base.card_accent_color = '#e11d48';
      base.title.color = '#9f1239';
      base.mp_link.color = '#be123c';
      base.group_header.color = '#be123c';
      break;
    case 'minimal_line':
      base.card_border_color = '#e2e8f0';
      base.card_bg_color = '#ffffff';
      base.card_accent_color = '#64748b';
      base.card_border_width_px = 0;
      base.card_padding_px = 12;
      base.title.size_px = 16;
      break;
    case 'classic':
      base.card_border_color = '#eceff3';
      base.card_bg_color = '#ffffff';
      base.card_accent_color = '#cbd5e1';
      base.card_border_width_px = 0;
      base.card_padding_px = 0;
      base.card_radius_px = 0;
      break;
    default:
      break;
  }

  return base;
}

function normalizePreset(raw: unknown): EntryStylePreset {
  const k = typeof raw === 'string' ? raw.trim() : '';
  const allowed: EntryStylePreset[] = [
    'card_teal',
    'card_amber',
    'border_navy',
    'soft_rose',
    'minimal_line',
    'classic',
  ];
  return (allowed.includes(k as EntryStylePreset) ? k : 'card_teal') as EntryStylePreset;
}

export function defaultEntryStyle(): EntryStyleConfig {
  return presetDefaults('card_teal');
}

export function entryStyleForPreset(preset: EntryStylePreset): EntryStyleConfig {
  return presetDefaults(preset);
}

export function mergeEntryStyleFromApi(raw: unknown): EntryStyleConfig {
  const inObj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const preset = normalizePreset(inObj.preset);
  const d = presetDefaults(preset);
  d.preset = preset;
  d.card_border_color = normColor(inObj.card_border_color, d.card_border_color);
  d.card_bg_color = normColor(inObj.card_bg_color, d.card_bg_color);
  d.card_accent_color = normColor(inObj.card_accent_color, d.card_accent_color);
  d.card_radius_px = Math.min(24, Math.max(0, Math.round(Number(inObj.card_radius_px ?? d.card_radius_px))));
  d.card_padding_px = Math.min(32, Math.max(0, Math.round(Number(inObj.card_padding_px ?? d.card_padding_px))));
  d.card_border_width_px = Math.min(
    4,
    Math.max(0, Math.round(Number(inObj.card_border_width_px ?? d.card_border_width_px))),
  );

  for (const key of ENTRY_STYLE_TEXT_KEYS.map((x) => x.key)) {
    const part =
      inObj[key] && typeof inObj[key] === 'object' ? (inObj[key] as Record<string, unknown>) : {};
    d[key] = defaultText({
      ...d[key],
      color: part.color,
      size_px: part.size_px !== undefined ? Number(part.size_px) : d[key].size_px,
      bold: part.bold !== undefined ? Boolean(part.bold) : d[key].bold,
      italic: part.italic !== undefined ? Boolean(part.italic) : d[key].italic,
      underline: part.underline !== undefined ? Boolean(part.underline) : d[key].underline,
    });
  }

  return d;
}

export function entryStyleTextCss(t: EntryStyleText): Record<string, string | number> {
  return {
    color: t.color,
    fontSize: `${t.size_px}px`,
    fontWeight: t.bold ? 700 : 400,
    fontStyle: t.italic ? 'italic' : 'normal',
    textDecoration: t.underline ? 'underline' : 'none',
    lineHeight: 1.6,
  };
}

export function entryStylePreviewCardStyle(style: EntryStyleConfig): Record<string, string | number> {
  const preset = style.preset;
  const base: Record<string, string | number> = {
    background: style.card_bg_color,
    borderRadius: style.card_radius_px,
    padding: style.card_padding_px,
    marginBottom: 12,
    overflow: 'hidden',
  };

  if (preset === 'classic') {
    return {
      ...base,
      paddingBottom: 18,
      borderBottom: `1px solid ${style.card_border_color}`,
      borderRadius: 0,
      padding: 0,
    };
  }

  if (preset === 'minimal_line') {
    return {
      ...base,
      borderTop: `3px solid ${style.card_accent_color}`,
    };
  }

  if (
    preset === 'card_teal' ||
    preset === 'card_amber' ||
    preset === 'border_navy' ||
    preset === 'soft_rose'
  ) {
    return {
      ...base,
      border:
        style.card_border_width_px > 0
          ? `${style.card_border_width_px}px solid ${style.card_border_color}`
          : undefined,
      borderLeft: `4px solid ${style.card_accent_color}`,
    };
  }

  return base;
}

export function entryStylePresetLabel(preset: EntryStylePreset): string {
  if (preset === 'classic') return '经典分隔';
  return ENTRY_STYLE_PRESETS_UI.find((p) => p.key === preset)?.label || preset;
}
